import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, onSessionExpired } from '../api/client';
import type { AuthUser, LogoutReason } from './types';

/** Estados possíveis durante o ciclo de vida da autenticação. */
export type AuthStatus = 'bootstrapping' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Erro/mensagem do último evento de logout involuntário. */
  lastLogoutReason: LogoutReason | null;
  login: (username: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Recarrega o usuário a partir de `/auth/me` — útil após mudança de perfil etc. */
  refreshMe: () => Promise<AuthUser | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthMeResponse {
  data: { user: AuthUser };
}

interface AuthTokenResponse {
  data: { user: AuthUser };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [lastLogoutReason, setLastLogoutReason] = useState<LogoutReason | null>(null);

  // Evita disparar múltiplos /auth/me concorrentes durante hydration ou refresh.
  const meInflight = useRef<Promise<AuthUser | null> | null>(null);

  const fetchMe = useCallback(async (): Promise<AuthUser | null> => {
    if (meInflight.current) return meInflight.current;
    const promise = (async () => {
      try {
        const res = await api.get<AuthMeResponse>('/v1/auth/me');
        return res.data.data.user;
      } catch {
        return null;
      } finally {
        meInflight.current = null;
      }
    })();
    meInflight.current = promise;
    return promise;
  }, []);

  // Bootstrap silencioso ao montar — restaura sessão se houver cookie válido.
  useEffect(() => {
    let cancelled = false;
    void fetchMe().then((u) => {
      if (cancelled) return;
      setUser(u);
      setStatus(u ? 'authenticated' : 'unauthenticated');
    });
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  // Registra listener global para 401 disparado pelo interceptor.
  // `onSessionExpired` retorna a função de unregister — usamos como cleanup
  // do useEffect (compatível com StrictMode + HMR sem zerar listeners alheios).
  useEffect(() => {
    return onSessionExpired((codigo) => {
      // Não derruba a sessão se já estamos no bootstrap ou deslogados.
      setUser((current) => {
        if (current === null) return null;
        setLastLogoutReason(codigo === 'SEM_PERMISSAO' ? 'forbidden' : 'session-expired');
        setStatus('unauthenticated');
        return null;
      });
    });
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<AuthUser> => {
    const res = await api.post<AuthTokenResponse>('/v1/auth/token', { username, password });
    const u = res.data.data.user;
    setUser(u);
    setStatus('authenticated');
    setLastLogoutReason(null);
    return u;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/v1/auth/logout');
    } catch {
      // Mesmo se o logout server falhar, limpa o estado local.
    }
    setUser(null);
    setStatus('unauthenticated');
    setLastLogoutReason('user');
  }, []);

  const refreshMe = useCallback(async (): Promise<AuthUser | null> => {
    const u = await fetchMe();
    setUser(u);
    setStatus(u ? 'authenticated' : 'unauthenticated');
    return u;
  }, [fetchMe]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, lastLogoutReason, login, logout, refreshMe }),
    [status, user, lastLogoutReason, login, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
